import { useEffect, useMemo, useRef } from "react"

import { getLogger } from "@/lib/logging.ts"

import type { Model } from "@/lib/Model.ts"


const log = getLogger("useModel")

export function useModel<T extends Model | null>(factory: () => T, deps: unknown[] = []): T {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const model = useMemo(factory, deps)

  useEffect(() => {
    if (!model) return

    log.debug(`Creating model ${model.debugName}`)

    return () => {
      if (!model.lifetime.terminated) {
        log.debug(`Terminating model ${model.debugName}`)
        model.terminate()
      }
    }
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps

  return model
}

export function useLazyModel<T extends Model>(
  condition: boolean,
  factory: () => T,
  deps: unknown[]
): T | null {
  const conditionRef = useRef(false)
  if (condition) conditionRef.current = true

  return useModel(() => (conditionRef.current ? factory() : null), deps)
}
